import React, { useEffect } from "react";
import { Auth } from "aws-amplify";
// ANT DESIGN COMPONENT
import { Drawer, Avatar, Button, Tag, Tooltip, Spin } from "antd";
// I18N TRANSLATION
import { useTranslation } from "react-i18next";
// FONT AWESOME LIBRARY AND ICONS
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleCheck, faRightFromBracket, faWifi } from "@fortawesome/free-solid-svg-icons";
// REDUX
import { useSelector, useDispatch } from "react-redux";
import {
  updateOpenDrawerUser,
  updateGlobalOrganizationName,
  updateGlobalRolName,
  updateGlobalOrganizationId,
  updateGlobalRolId,
} from "../../redux/home/homeSlice";
import { useLazyGetRoleQuery } from "../../redux/roles/roleAPI";
import { updatePermissions } from "../../redux/users/userSlice";
import { updateClearCascader } from "../../redux/monitoring/monitoringSlice";
// COMPONENTS
import Appearance from "./Appearance";
import Internationalization from "./Internationalization";
import DrawerOrganizationInformation from "../components/drawer/DrawerOrganizationInformation";
import DrawerRoleInformation from "../components/drawer/DrawerRoleInformation";
import DrawerUserInformation from "../components/drawer/DrawerUserInformation";
import GlobalConfiguration from "./GlobalConfiguration";
import LiftnetInformation from "./LiftnetInformation";
import ButtonClose from "../components/buttons/ButtonClose";
import SelectOrganizationsLight from "../components/select/SelectOrganizationsLight";
import SelectRolesLight from "../components/select/SelectRolesLight";
import GlobalAlert2 from "./GlobalAlert2";
import AppNotifications from "./AppNotifications";
// STYLES
import usaFlag from "../../assets/i18n/unitedStatesFlag.png";
import spainFlag from "../../assets/i18n/spainFlag.png";
// INTERFACES
import { RootState } from "../../app/store";
// AUTHORIZATION
import GETJwtToken from "../../redux/authentication/authentication";

const YourProfileDrawer = () => {
  // ************************************************ */
  // GLOBAL VARIABLES ******************************* */
  const dispatch = useDispatch();
  const [t] = useTranslation("global");

  // ************************************************ */
  // USE STATE VARIABLES **************************** */

  // ************************************************ */
  // REDUX SLICE VARIABLES ************************** */
  const {
    userLogged,
    language,
    openDrawerUser,
    globalOrganizationName,
    globalRolName,
    globalOrganizationId,
    globalRolId,
  } = useSelector((state: RootState) => state.home);
  const { socketConnection, showHeaderConfiguration } = useSelector(
    (state: RootState) => state.monitoring,
  );

  // ************************************************ */
  // SERVICES AND API CALLS ************************* */
  const [
    triggerGetAdaptersTable,
    {
      isLoading: isLoadingGetRole,
      isFetching: isFetchingGetRole,
      isError: isErrorGetRole,
      error: errorGetRole,
    },
  ] = useLazyGetRoleQuery();

  // ************************************************* */
  // FUNCTIONS *************************************** */
  const showDrawer = () => {
    dispatch(updateOpenDrawerUser(true));
  };

  const onClickClose = () => {
    dispatch(updateOpenDrawerUser(false));
  };

  const signOut = async () => {
    await Auth.signOut();
    localStorage.setItem("useLoggedAmplify", "false");
    window.location.href = `${process.env.REACT_APP_LOGIN_ROUTE}`;
  };

  const onChangeIdOrganization = (idOrganization: string, record: any) => {
    dispatch(updateGlobalOrganizationId(record.value));
    dispatch(updateGlobalOrganizationName(record.children));
    dispatch(updateClearCascader(true));
  };

  const onChangeIdRole = async (newValue: string) => {
    try {
      const token = await GETJwtToken();
      const result = await triggerGetAdaptersTable({ token, roleId: newValue });
      if (result && result.data && result.data.data) {
        // setJsonObjectPermissions(JSON.stringify(result.data.data.menu));
        dispatch(updatePermissions(result.data.data.menu));
        dispatch(updateGlobalRolId(result.data.data.id));
        dispatch(updateGlobalRolName(result.data.data.name));
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
    }
  };

  const onClickResetToMasterAdmin = () => {
    dispatch(updateGlobalOrganizationId(userLogged.org_id));
    dispatch(updateGlobalOrganizationName(userLogged.org.name));
    onChangeIdRole(userLogged.role_id);
  };

  // *********************************************** */
  // USE EFFECT ************************************ */
  useEffect(() => {
    if (userLogged) {
      if (userLogged.org && userLogged.org.name) {
        dispatch(updateGlobalOrganizationId(userLogged.org.id));
        dispatch(updateGlobalOrganizationName(userLogged.org.name));
      }
      if (userLogged.role && userLogged.role.name) {
        dispatch(updateGlobalRolId(userLogged.role.id));
        dispatch(updateGlobalRolName(userLogged.role.name));
      }
    }
  }, [userLogged]);

  // ************************************************* */
  // COMPONENT ************************************** */
  return (
    <div>
      <Spin spinning={isLoadingGetRole || isFetchingGetRole}>
        <div className="generalStyles__flex">
          {/** ************************************************* */}
          {/** HEADER AND TAGS */}
          <div className="mt-2 generalStyles__flex">
            {userLogged &&
            userLogged.role &&
            userLogged.role.name === "MASTER ADMIN" &&
            showHeaderConfiguration ? (
              <div className="generalStyles__flex" style={{ marginTop: "-6px" }}>
                <div className="generalStyles__width200px generalStyles__mrFix">
                  <SelectOrganizationsLight
                    value={globalOrganizationId}
                    onChangeIdOrganization={onChangeIdOrganization}
                    disabledInput={false}
                  />
                </div>
                <div className="generalStyles__width200px generalStyles__mrFix">
                  <SelectRolesLight
                    value={globalRolId}
                    onChangeIdRole={onChangeIdRole}
                    disabledInput={false}
                  />
                </div>
                <div className="generalStyles__mrFix">
                  <Button
                    size="small"
                    className="buttonStyle__1"
                    style={{ width: "100%" }}
                    onClick={onClickResetToMasterAdmin}
                  >
                    {t("general.reset")}
                  </Button>
                </div>
              </div>
            ) : (
              <>
                <div>
                  <Tooltip title={globalOrganizationName}>
                    <Tag
                      color="blue"
                      className="generalStyles__width150px generalStyles__textCenter generalStyles__textEllipsis"
                    >
                      {globalOrganizationName || "--"}
                    </Tag>
                  </Tooltip>
                </div>
                <div>
                  <Tooltip title={globalRolName}>
                    <Tag
                      color="purple"
                      className="generalStyles__width150px generalStyles__textCenter generalStyles__textEllipsis"
                    >
                      {globalRolName || "--"}
                    </Tag>
                  </Tooltip>
                </div>
              </>
            )}
            {socketConnection === "EDGE" && (
              <div>
                <Tag
                  color="green"
                  className="generalStyles__width150px generalStyles__textCenter generalStyles__textEllipsis"
                >
                  <FontAwesomeIcon icon={faWifi} className=" generalStyles__userLoggedIcon" />
                  {"  "}Edge Server
                </Tag>
              </div>
            )}
            {socketConnection === "OFFLINE" && (
              <div>
                <Tag
                  color="cyan"
                  className="generalStyles__width150px generalStyles__textCenter generalStyles__textEllipsis"
                >
                  <FontAwesomeIcon icon={faWifi} className=" generalStyles__userLoggedIcon" />
                  {"  "}Offline Server
                </Tag>
              </div>
            )}
            {socketConnection === "EMULATOR" && (
              <div>
                <Tag
                  color="orange"
                  className="generalStyles__width150px generalStyles__textCenter generalStyles__textEllipsis"
                >
                  <FontAwesomeIcon icon={faWifi} className=" generalStyles__userLoggedIcon" />
                  {"  "}Emulator Server
                </Tag>
              </div>
            )}
          </div>
          {/** ************************************************* */}
          {/** USER AVATAR */}
          <div
            className="generalStyles__mlFix25"
            role="button"
            tabIndex={0}
            onClick={() => {
              showDrawer();
            }}
            onKeyDown={() => {
              showDrawer();
            }}
          >
            <Avatar className="button__button1">
              {userLogged && userLogged.first_name ? userLogged.first_name.substring(0, 1) : ""}
              {userLogged && userLogged.last_name ? userLogged.last_name.substring(0, 1) : ""}
            </Avatar>
          </div>
          {/** ************************************************* */}
          {/** CURRENT LANGUAGE ICON */}
          <div>
            <img
              src={language === "en" ? usaFlag : spainFlag}
              alt=""
              width="25"
              height="15"
              className="generalStyles__mlFix"
            />
          </div>
        </div>
      </Spin>
      <Drawer
        width={550}
        placement="right"
        onClose={onClickClose}
        closable={false}
        open={openDrawerUser}
        footer={
          <div className="container generalStyles__flex">
            <div className="generalStyles__width100 generalStyles__mrFix">
              <ButtonClose onClick={onClickClose} />
            </div>
          </div>
        }
      >
        <div className="generalStyles__spaceBetween">
          <div className="generalStyles__flex">
            {/** ************************************************* */}
            {/** USER AVATAR */}
            <div>
              <Avatar size={{ xs: 24, sm: 32, md: 40, lg: 64, xl: 80, xxl: 100 }}>
                {userLogged && userLogged.first_name ? userLogged.first_name.substring(0, 1) : ""}
                {userLogged && userLogged.last_name ? userLogged.last_name.substring(0, 1) : ""}
              </Avatar>
            </div>
            {/** ************************************************* */}
            {/** USER EMAIL AND ROLE DATA */}
            <div className="generalStyles__mlFix25 mt-3">
              <p className="drawer__userNameDrawer">
                {userLogged && userLogged.first_name ? userLogged.first_name : ""}{" "}
                {userLogged && userLogged.middle_name ? userLogged.middle_name : ""}{" "}
                {userLogged && userLogged.last_name ? userLogged.last_name : ""}
              </p>
              <p className="generalStyles__userLoggedEmail">
                {userLogged && userLogged.email ? userLogged.email : "--"}
              </p>
              <p className="generalStyles__userLoggedEmail">
                <FontAwesomeIcon
                  icon={faCircleCheck}
                  className="generalStyles__success generalStyles__userLoggedIcon"
                />
                <span className="generalStyles__mlFix">
                  {" "}
                  {userLogged && userLogged.role && userLogged.role.name
                    ? userLogged.role.name
                    : "--"}
                </span>
              </p>
            </div>
          </div>
          {/** ************************************************* */}
          {/** SIGN OUT BUTTON */}
          <div>
            <Button
              className="drawer__logoutButton"
              shape="round"
              icon={<FontAwesomeIcon icon={faRightFromBracket} className="generalStyles__mrFix" />}
              onClick={signOut}
            >
              {t("header.signOut")}
            </Button>
          </div>
        </div>
        {/** ************************************************* */}
        {userLogged && (
          <div>
            {/** ************************************************* */}
            {/** PASSWORD OFFLINE */}
            {/** <OfflinePsw /> */}
            {/** ************************************************* */}
            {/** GLOBAL CONFIGURATION */}
            {userLogged &&
              userLogged.role &&
              userLogged.role.name &&
              userLogged.role.name === "MASTER ADMIN" && <GlobalConfiguration />}
            {/** ************************************************* */}
            {/** NOTIFICATIONS */}
            <AppNotifications />
            {/** ************************************************* */}
            {/** INTERNATIONALIZATION */}
            <Internationalization />
            {/** ************************************************* */}
            {/** APPEARANCE */}
            <Appearance />
            {/** ************************************************* */}
            {/** USER INFORMATION */}
            <DrawerUserInformation data={userLogged || {}} />
            {/** ************************************************* */}
            {/** ROLE INFORMATION */}
            <DrawerRoleInformation data={userLogged.role || {}} />
            {/** ************************************************* */}
            {/** ORGANIZATION INFORMATION */}
            <DrawerOrganizationInformation data={userLogged.org || {}} />
            {/** ************************************************* */}
            {/** LIFTNET INFORMATION */}
            <LiftnetInformation />
          </div>
        )}
        <br />
      </Drawer>
      <GlobalAlert2
        isError={isErrorGetRole}
        isSuccess={false}
        requestType="GET"
        error={errorGetRole}
        name="Role"
      />
    </div>
  );
};

export default YourProfileDrawer;
